import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Keyboard,
  Platform,
  ScrollView,
  TouchableWithoutFeedback,
  useColorScheme,
  useWindowDimensions,
} from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from 'styled-components/native';
import { RFValue } from 'react-native-responsive-fontsize';

import BottomSheet from '../../Interface/BottomSheet';
import { Button } from '../../Interface/Forms/Buttons/Button';
import { ProductDTO } from '../../../dtos/ProductDTO';
import { useRemoteConfig } from '../../../context/useRemoteConfig';
import {
  ContentContainer,
  Title,
  Subtitle,
  LitrosInputContainer,
  LitrosRow,
  LitrosButton,
  LitrosButtonIcon,
  LitrosInput,
  LitrosUnit,
  ButtonsContainer,
  PricePreview,
  InfoTriggerButton,
  InfoTriggerLeft,
  InfoTriggerIconCircle,
  InfoTriggerIcon,
  InfoTriggerTextBlock,
  InfoTriggerLabel,
  InfoTriggerSub,
  InfoTriggerChevron,
  InfoSheetContent,
  InfoSheetIconCircle,
  InfoSheetMainIcon,
  InfoSheetTitle,
  InfoSheetDesc,
  InfoStepCard,
  InfoStepIconBox,
  InfoStepIcon,
  InfoStepBody,
  InfoStepCardTitle,
  InfoStepCardText,
  InfoExampleBox,
  InfoExampleAccent,
  InfoExampleInner,
  InfoExampleLabel,
  InfoExampleText,
  InfoExampleBold,
} from './styles';

const MAX_LITERS = 1000;

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export interface EncherTanqueBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  fuelPrice: number;
  currentFuel: ProductDTO;
  onConfirmQuantity: (liters: number) => void;
  onPayment: (params: { quantity: number; amount: number }) => void;
}

export function EncherTanqueBottomSheet({
  isOpen,
  onClose,
  fuelPrice,
  currentFuel,
  onConfirmQuantity,
  onPayment,
}: EncherTanqueBottomSheetProps) {
  const theme = useTheme();
  const colorScheme = useColorScheme();
  const isDarkMode = colorScheme === 'dark';
  const { height: screenHeight } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const availableHeight = screenHeight - insets.top - insets.bottom;
  const [litros, setLitros] = useState(20);
  const [showInfoSheet, setShowInfoSheet] = useState(false);
  const [inputText, setInputText] = useState('20');
  const [isInputFocused, setIsInputFocused] = useState(false);

  const {
    encherTanqueInfoTitle,
    encherTanqueInfoDesc,
    encherTanqueInfoSteps,
    encherTanqueInfoExample,
  } = useRemoteConfig(showInfoSheet);

  useEffect(() => {
    if (isOpen) {
      setLitros(20);
      setInputText('20');
      setIsInputFocused(false);
      setShowInfoSheet(false);
    }
  }, [isOpen]);

  const clampAndSetLitros = useCallback((value: number) => {
    const clamped = Math.max(1, Math.min(MAX_LITERS, Math.floor(value)));
    setLitros(clamped);
    setInputText(String(clamped));
  }, []);

  const handleLitrosChange = useCallback(
    (delta: number) => {
      Keyboard.dismiss();
      clampAndSetLitros(litros + delta);
    },
    [litros, clampAndSetLitros],
  );

  const handleInputChange = useCallback((text: string) => {
    const numericOnly = text.replace(/[^0-9]/g, '');
    if (!numericOnly) {
      setInputText('');
      return;
    }

    const num = parseInt(numericOnly, 10);
    if (isNaN(num)) {
      setInputText('');
      return;
    }

    const clamped = Math.min(num, MAX_LITERS);
    setLitros(clamped);
    setInputText(num > MAX_LITERS ? String(MAX_LITERS) : numericOnly);
  }, []);

  const handleInputBlur = useCallback(() => {
    setIsInputFocused(false);
    const parsed = parseInt(inputText, 10);
    clampAndSetLitros(isNaN(parsed) || parsed < 1 ? 1 : parsed);
  }, [inputText, clampAndSetLitros]);

  const handleInputFocus = useCallback(() => setIsInputFocused(true), []);

  const handleConfirm = useCallback(() => {
    Keyboard.dismiss();
    const amount = litros * fuelPrice;
    onConfirmQuantity(litros);
    onPayment({ quantity: litros, amount });
    onClose();
  }, [litros, fuelPrice, onConfirmQuantity, onPayment, onClose]);

  const handleBack = useCallback(() => {
    Keyboard.dismiss();
    onClose();
  }, [onClose]);

  const displayValue = isInputFocused ? inputText : String(litros);
  const canConfirm = litros >= 1;
  const totalAmount = useMemo(
    () => formatCurrency(litros * fuelPrice),
    [litros, fuelPrice],
  );

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      {showInfoSheet ? (
        <>
          <ScrollView
            style={{ maxHeight: availableHeight * 0.75 }}
            contentContainerStyle={{ paddingBottom: RFValue(8) }}
            showsVerticalScrollIndicator={false}>
            <InfoSheetContent>
              <InfoSheetIconCircle>
                <InfoSheetMainIcon name="gas-pump" />
              </InfoSheetIconCircle>

              <InfoSheetTitle>{encherTanqueInfoTitle}</InfoSheetTitle>
              <InfoSheetDesc>{encherTanqueInfoDesc}</InfoSheetDesc>

              {[
                { accent: 'rgba(255,59,48,0.12)', icon: 'droplet', iconAccent: theme.colors.red },
                { accent: 'rgba(52,199,89,0.12)',  icon: 'tag',     iconAccent: '#34C759' },
                { accent: 'rgba(255,149,0,0.12)',  icon: 'zap',     iconAccent: '#FF9500' },
              ].map(({ accent, icon, iconAccent }, i) => (
                <InfoStepCard key={i}>
                  <InfoStepIconBox accent={accent}>
                    <InfoStepIcon name={icon} accent={iconAccent} />
                  </InfoStepIconBox>
                  <InfoStepBody>
                    <InfoStepCardTitle>
                      {encherTanqueInfoSteps[i]?.title}
                    </InfoStepCardTitle>
                    <InfoStepCardText>
                      {encherTanqueInfoSteps[i]?.text}
                    </InfoStepCardText>
                  </InfoStepBody>
                </InfoStepCard>
              ))}

              <InfoExampleBox>
                <InfoExampleAccent />
                <InfoExampleInner>
                  <InfoExampleLabel>EXEMPLO</InfoExampleLabel>
                  <InfoExampleText>
                    {encherTanqueInfoExample.map((item, i) =>
                      item.type === 'textBold' ? (
                        <InfoExampleBold key={i}>{item.value}</InfoExampleBold>
                      ) : (
                        item.value
                      ),
                    )}
                  </InfoExampleText>
                </InfoExampleInner>
              </InfoExampleBox>
            </InfoSheetContent>
          </ScrollView>

          <InfoSheetContent style={{ paddingTop: 0 }}>
            <Button
              title="Entendi"
              onPress={() => setShowInfoSheet(false)}
              enabled
            />
          </InfoSheetContent>
        </>
      ) : (
        <ContentContainer>
          <Title>Quantos litros?</Title>
          <Subtitle>
            {currentFuel.name} · {formatCurrency(fuelPrice)}/L
          </Subtitle>

          <LitrosInputContainer>
            <LitrosRow>
              <LitrosButton
                disabled={litros <= 1}
                activeOpacity={0.7}
                onPress={() => handleLitrosChange(-1)}>
                <LitrosButtonIcon name="minus" />
              </LitrosButton>

              <LitrosInput
                value={displayValue}
                onChangeText={handleInputChange}
                onBlur={handleInputBlur}
                onFocus={handleInputFocus}
                keyboardType="number-pad"
                maxLength={4}
                placeholder="0"
                placeholderTextColor={theme.colors.placeholder}
                selectTextOnFocus
                style={{
                  borderBottomColor: isInputFocused
                    ? theme.colors.red
                    : theme.colors.border,
                }}
              />

              <LitrosButton
                disabled={litros >= MAX_LITERS}
                activeOpacity={0.7}
                onPress={() => handleLitrosChange(1)}>
                <LitrosButtonIcon name="plus" />
              </LitrosButton>
            </LitrosRow>
            <LitrosUnit>litros</LitrosUnit>
          </LitrosInputContainer>

          <InfoTriggerButton
            activeOpacity={0.7}
            onPress={() => setShowInfoSheet(true)}>
            <InfoTriggerLeft>
              <InfoTriggerIconCircle>
                <InfoTriggerIcon name="info" />
              </InfoTriggerIconCircle>
              <InfoTriggerTextBlock>
                <InfoTriggerLabel>Garantia de restituição</InfoTriggerLabel>
                <InfoTriggerSub>Saiba o que acontece se sobrar litros</InfoTriggerSub>
              </InfoTriggerTextBlock>
            </InfoTriggerLeft>
            <InfoTriggerChevron name="chevron-right" />
          </InfoTriggerButton>

          <ButtonsContainer>
            <Button
              title="Confirmar"
              onPress={handleConfirm}
              enabled={canConfirm}
            />
            <Button
              title="Voltar"
              onPress={handleBack}
              enabled
              {...(isDarkMode
                ? { outline: true, color: theme.colors.shape }
                : { light: true })}
            />
          </ButtonsContainer>
        </ContentContainer>
      )}
    </BottomSheet>
  );
}
